import React from "react";
import { Link, useLocation } from "react-router-dom";
import { styled } from "@mui/material/styles";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell, { tableCellClasses } from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import { Stack } from "@mui/material";

import RenderAppraiser from "./RenderAppraiser";
import RenderMeetingDate from "./RenderMeetingDate";

import AppraisalStatusIcon from "./AppraisalStatusIcon";
import AppraisalFormStatusIcon from "./AppraisalFormStatusIcon";
import RenderDate from "../../../Common/Date/RenderDate";

const StyledTableCell = styled(TableCell)(({ theme }) => ({
    [`&.${tableCellClasses.head}`]: {
        backgroundColor: theme.palette.primary.main,
        color: theme.palette.common.white,
        padding: 12,
    },
    [`&.${tableCellClasses.body}`]: {
        fontSize: 18,
    },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
    "&:nth-of-type(odd)": {
        backgroundColor: theme.palette.action.hover,
    },
    // hide last border
    "&:last-child td, &:last-child th": {
        border: 0,
    },
}));

const LinkBuilder = ({ text, id, model, appraisee }) => {
    const location = useLocation();
    const refererPath = encodeURIComponent(location.pathname);

    const label = text?.length > 0 ? text : "No title";

    let display = label;
    const appraiseeUUID = appraisee?.uuid ?? null;

    if (id) {
        display = <Link to={`/rms/appraisals/${Number(id)}`}>{label}</Link>;

        if (model === "review") {
            if (appraiseeUUID) {
                display = (
                    <Link to={`/rms/appraisals/${Number(id)}/${appraiseeUUID}/?rp=${refererPath}`}>
                        {label}
                    </Link>
                );
            } else {
                display = label;
            }
        }
    }

    return display;
};

function AppraisalTable({items = [], model = null, appraisee = null, isAppraiser = false}) {
    return (
        <TableContainer>
            <Table sx={{ minWidth: 500 }} aria-label="customized table">
                <TableHead>
                    <TableRow>
                        <StyledTableCell>Appraisal</StyledTableCell>
                        <StyledTableCell>Appraiser&nbsp;</StyledTableCell>
                        <StyledTableCell align="center">Meeting</StyledTableCell>
                        <StyledTableCell align="center">Due</StyledTableCell>
                        <StyledTableCell align="right">Progress&nbsp;</StyledTableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {Array.isArray(items) &&
                        items
                            .sort((a, b) => (b?.meeting?.date || 0) - (a?.meeting?.date || 0))
                            .map((row) => (
                                <StyledTableRow key={row?.id}>
                                    <StyledTableCell>
                                        <LinkBuilder
                                            text={row?.title}
                                            id={row?.id}
                                            model="review"
                                            appraisee={appraisee}
                                        />
                                    </StyledTableCell>
                                    <StyledTableCell>
                                        <RenderAppraiser
                                            appraiser={row?.appraiser}
                                            isCompleted={row?.status === "completed"}
                                        />
                                    </StyledTableCell>
                                    <StyledTableCell align="center">
                                        <RenderMeetingDate
                                            meeting={row?.meeting}
                                            isCompleted={row?.status === "completed"}
                                        />
                                    </StyledTableCell>
                                    <StyledTableCell align="center">
                                        <RenderDate
                                            date={row?.dateDue}
                                            colourise={!row?.status === "completed"}
                                        />
                                    </StyledTableCell>
                                    <StyledTableCell align="right">
                                        <Stack direction="row" justifyContent="flex-end" spacing={0} >
                                            <AppraisalFormStatusIcon
                                                status={row?.inputs?.status}
                                                form="inputs"
                                                isAppraiser={isAppraiser}
                                            />
                                            <AppraisalStatusIcon
                                                status={row?.meeting?.status}
                                                section="meeting"
                                            />
                                            <AppraisalFormStatusIcon
                                                status={row?.outputs?.status}
                                                form="outputs"
                                                isAppraiser={isAppraiser}
                                            />
                                            <AppraisalStatusIcon
                                                status={row?.survey?.status}
                                                section={row?.survey?.name || "survey"}
                                            />
                                            <AppraisalStatusIcon
                                                status={row?.status}
                                                section="appraisal"
                                            />
                                        </Stack>
                                    </StyledTableCell>
                                </StyledTableRow>
                            ))}
                    <StyledTableRow key="totals">
                        <StyledTableCell component="th" scope="row" sx={{ backgroundColor: "#FFFFFF" }} colSpan={3} />
                    </StyledTableRow>
                </TableBody>
            </Table>
        </TableContainer>
    );
}
export default AppraisalTable;
